import {
  faArrowUpRight,
  faBrakeWarning,
  faChevronDown,
  faDownload,
  faShieldXmark,
} from '@fortawesome/pro-regular-svg-icons'
import { ActionIcon, Button, Flex, Menu, Text, Tooltip } from '@mantine/core'
import { useRouter } from 'next/router'
import { useMemo } from 'react'

import { useAnalytics } from '@flecto/analytics'
import { useTranslation } from '@flecto/translations'
import { type CoreInsurance } from '@flecto/types'
import {
  DataTable,
  type DataTableColumn,
  type DataTableProps,
  Icon,
  TextCurrency,
  TextDate,
} from '@flecto/ui'

import { BadgeInsuranceStatus } from '../../../booking-insurances/components/BadgeInsuranceStatus/BadgeInsuranceStatus'
import {
  EKindTab,
  useInsuranceManagerState,
} from '../../../booking-sidebar/state/index'
import { useModalConfirmationCancelInsurance } from '../../hooks/useModalConfirmationCancelInsurance'
import { BadgeInsurancePolicy } from '../BadgeInsurancePolicy/BadgeInsurancePolicy'
import { CellName } from './CellName'

export type TableInsuranceListProps = Omit<
  DataTableProps<CoreInsurance>,
  'columns'
> & {
  insurances?: CoreInsurance[]
}

export function TableInsuranceList({
  insurances = [],
  ...props
}: TableInsuranceListProps) {
  const router = useRouter()
  const track = useAnalytics('segment')

  const { openModal } = useModalConfirmationCancelInsurance()

  const { t } = useTranslation('saas', { keyPrefix: 'insurances' })

  const { setTab, setModalOpened } = useInsuranceManagerState()

  const handleRowClick = async (insurances: CoreInsurance) => {
    await router.push(`/insurances/${insurances.internal_id}`)
  }

  const handleButtonClick = async (insurances: CoreInsurance) => {
    if (insurances?.booking?.internal_id) {
      await router.push(`/bookings/${insurances.booking.internal_id}`)
      setModalOpened(true)
      setTab(EKindTab.ISSUES)
    }
  }

  const columns: DataTableColumn<CoreInsurance>[] = useMemo(
    () => [
      {
        accessor: 'id',
        title: t('table.label_insurance_id'),
        sortable: true,
        render: (row) => (
          <Text c={'gray.7'}>{row.id ? `#${row.internal_id}` : '-'}</Text>
        ),
      },
      {
        accessor: 'booking_id',
        title: t('table.label_booking_id'),
        width: 120,
        sortable: true,
        render: (row) => (
          <Text c={'gray.7'}>
            {row.booking?.internal_id ? `#${row.booking.internal_id}` : '-'}
          </Text>
        ),
      },
      {
        accessor: 'item_id',
        title: t('table.label_product'),
        width: 320,
        sortable: true,
        render: (row) => <CellName insurance={row} />,
      },
      {
        accessor: 'created_at',
        title: t('table.label_issue_date'),
        sortable: true,
        width: 150,
        render: (row) => (
          <Flex wrap='nowrap'>
            <TextDate
              date={row.created_at}
              variant='datetime-brief'
              style={{ flexShrink: 0 }}
            />
          </Flex>
        ),
      },
      {
        accessor: 'meta_status',
        title: t('table.label_policy_status'),
        sortable: true,
        render: (row) => <BadgeInsuranceStatus insurance={row} withDot />,
      },
      {
        accessor: 'price_cents',
        title: t('table.label_insurance_cost'),
        sortable: true,
        render: (row) => <TextCurrency cents={row.price_cents} />,
      },
      {
        accessor: 'start_at',
        title: t('table.label_start_date'),
        sortable: true,
        width: 150,
        render: (row) => (
          <Flex wrap='nowrap'>
            <TextDate
              date={row.start_at}
              variant='datetime-brief'
              style={{ flexShrink: 0 }}
            />
          </Flex>
        ),
      },
      {
        accessor: 'end_at',
        title: t('table.label_end_date'),
        sortable: true,
        width: 150,
        render: (row) => (
          <Flex wrap='nowrap'>
            <TextDate
              date={row.end_at}
              variant='datetime-brief'
              style={{ flexShrink: 0 }}
            />
          </Flex>
        ),
      },
      {
        accessor: 'policy_type',
        title: t('table.label_policy_type'),
        sortable: true,
        render: (row) =>
          row.policy_type ? (
            <BadgeInsurancePolicy policyType={row.policy_type || ''} />
          ) : (
            <Text c={'gray.7'}>{'-'}</Text>
          ),
      },
      {
        accessor: 'notes',
        title: t('table.label_notes'),
        render: (row) => {
          const content = row.notes ? row.notes.replace(/<[^>]*>?/gm, '') : ''

          return content ? (
            <Tooltip
              label={
                <Text style={{ whiteSpace: 'break-spaces' }}>{content}</Text>
              }
              maw={250}
            >
              <Text truncate>{content}</Text>
            </Tooltip>
          ) : (
            '-'
          )
        },
        width: 250,
      },
      {
        accessor: 'actions',
        title: '',
        render: (row) => (
          <Flex gap='sm'>
            {row.meta_status === 'scheduled' ? (
              <Button
                variant='default'
                leftSection={
                  <Icon icon={faShieldXmark} size='sm' color='gray.7' />
                }
                onClick={(e) => {
                  openModal({ insurance: row })
                  e.stopPropagation()
                  track('INSURANCE_TABLE__QUICK_ACTION', {
                    action: 'cancel',
                  })
                }}
                size='sm'
              >
                {t('table.cancel_btn')}
              </Button>
            ) : (
              <Button
                disabled={
                  row.meta_status === 'canceled' ||
                  row.meta_status === 'expired'
                }
                variant='default'
                leftSection={
                  <Icon icon={faBrakeWarning} size='md' color='gray.7' />
                }
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  track('INSURANCE_TABLE__QUICK_ACTION', {
                    action: 'claim',
                  })
                }}
                href={row.claim_url}
                component={
                  row.meta_status === 'canceled' ||
                  row.meta_status === 'expired'
                    ? 'button'
                    : 'a'
                }
                target='_blank'
                size='sm'
              >
                {t('table.claim_btn')}
              </Button>
            )}

            <Menu>
              <Menu.Target>
                <ActionIcon
                  onClick={(e) => e.stopPropagation()}
                  variant='default'
                  size='sm'
                >
                  <Icon icon={faChevronDown} size='xs' color='gray.7' />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  disabled={!row.booking?.internal_id}
                  onClick={(e) => {
                    handleButtonClick(row)
                    e.stopPropagation()
                  }}
                  leftSection={<Icon icon={faArrowUpRight} size='sm' />}
                >
                  {t('table.open_booking_btn')}
                </Menu.Item>
                <Menu.Item
                  component='a'
                  href={row.terms_url}
                  target='_blank'
                  leftSection={<Icon icon={faDownload} size='sm' />}
                  onClick={(e) => {
                    e.stopPropagation()
                  }}
                >
                  {t('table.terms_and_conditions_btn')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Flex>
        ),
      },
    ],
    [t]
  )

  return (
    <DataTable
      {...props}
      records={insurances}
      columns={columns}
      highlightOnHover
      horizontalSpacing='md'
      onRowClick={({ record: insurances }) => handleRowClick(insurances)}
      pinLastColumn
    />
  )
}
